import React from 'react';
import {
  Box, Paper, Typography, Pagination,
} from '@mui/material';
import { JournalEntry, Pagination as PaginationType, KpiData, SortField, Order } from '../types';
import { DEFAULT_PAGE_LIMIT } from '../constants';
import { ColumnDef } from '../hooks/useColumnVisibility';
import { fmtSum } from '../utils';
import HeaderCell from './HeaderCell';
import SkeletonRow from './SkeletonRow';
import JournalTableRow from './JournalTableRow';
import EmptyState from './shared/EmptyState';

interface JournalTableProps {
  entries: JournalEntry[];
  tableLoading: boolean;
  pagination: PaginationType | null;
  kpi: KpiData | null;
  page: number;
  orderBy: SortField;
  order: Order;
  onSort: (field: SortField) => void;
  onPageChange: (page: number) => void;
  filterParams: Record<string, string>;
  selectedMonth: string;
  monthsList: { value: string; label: string }[];
  pharmacies: string[];
  /** Только видимые колонки (из useColumnVisibility) */
  visibleDefs: ColumnDef[];
  /** CSS grid-template-columns для видимых колонок */
  gridTemplate: string;
}

/** Маппинг id колонки → sortField (undefined = не сортируется) */
const SORT_FIELD_MAP: Partial<Record<string, SortField>> = {
  pharmacy:   'pharmacyNum',
  region:     'cityId',
  name:       'materialName',
  expiry:     'expiryDate',
  serial:     'serialNumber',
  qty:        'quantity',
  qtyPcs:     'quantityPcs',
  price:      'price',
  totalSum:   'totalSum',
  manager:    'managerName',
  category:   'categoryName',
  priceGroup: 'priceGroup',
  code:       'nomenclatureCode',
  barcode:    'barcode',
  rxotc:      'rxotc' as SortField,
  comment:    'comment',
  recordDate: 'recordDate',
  createdBy:  'createdBy',
};



/**
 * Полная таблица журнала: заголовок, строки данных и футер с пагинацией.
 * Поддерживает управление видимостью колонок и sticky-закрепление первых колонок.
 */
const JournalTable: React.FC<JournalTableProps> = ({
  entries, tableLoading, pagination, kpi, page,
  orderBy, order, onSort, onPageChange,
  filterParams, selectedMonth, monthsList, pharmacies,
  visibleDefs, gridTemplate,
}) => {
  const getEmptyMessage = () => {
    if (Object.keys(filterParams).length > 0) return 'Нет записей по выбранным фильтрам';
    if (selectedMonth !== 'all') {
      const label = monthsList.find(m => m.value === selectedMonth)?.label ?? selectedMonth;
      return `В периоде «${label}» нет записей`;
    }
    return 'Журнал пуст';
  };

  return (
    <Paper elevation={0} variant="outlined" sx={{ width: '100%', overflow: 'hidden', borderRadius: 2 }}>
      <Box sx={{
        overflowX: 'auto', maxHeight: '55vh', overflowY: 'auto',
        '&::-webkit-scrollbar': { height: 6 },
      }}>
        <Box sx={{ minWidth: 'max-content' }}>
          {/* ── Заголовок ── */}
          <Box sx={{
            display: 'grid', gridTemplateColumns: gridTemplate,
            gap: 1, px: 2, py: 1,
            borderBottom: '1px solid', borderColor: 'divider',
            bgcolor: 'grey.50', alignItems: 'center',
            position: 'sticky', top: 0, zIndex: 10,
          }}>
            {visibleDefs.map(col => {
              const sortField = SORT_FIELD_MAP[col.id];
              const isRightAligned = ['qty', 'qtyPcs', 'price', 'totalSum'].includes(col.id);
              return (
                <Box
                  key={col.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <HeaderCell
                    orderBy={orderBy}
                    order={order}
                    sortField={sortField}
                    onSort={sortField ? onSort : undefined}
                    align={isRightAligned ? 'right' : 'left'}
                  >
                    {col.label}
                  </HeaderCell>
                </Box>
              );
            })}
          </Box>

          {/* ── Строки ── */}
          {tableLoading
            ? Array.from({ length: 8 }).map((_, i) => (
              <SkeletonRow key={i} visibleCount={visibleDefs.length} gridTemplate={gridTemplate} />
            ))
            : entries.length === 0
              ? <EmptyState message={getEmptyMessage()} />
              : entries.map((row, idx) => (
                <JournalTableRow
                  key={row.id ?? idx}
                  row={row}
                  globalIdx={(page - 1) * DEFAULT_PAGE_LIMIT + idx + 1}
                  pharmacies={pharmacies}
                  visibleDefs={visibleDefs}
                  gridTemplate={gridTemplate}
                />
              ))
          }
        </Box>
      </Box>

      {/* ── Футер с пагинацией ── */}
      {!tableLoading && pagination && pagination.totalCount > 0 && (
        <Box sx={{
          px: 2, py: 1.5,
          borderTop: '1px solid', borderColor: 'divider',
          bgcolor: 'grey.50',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexWrap: 'wrap', gap: 1,
        }}>
          <Typography variant="body2" color="text.secondary">
            Записей: <strong>{pagination.totalCount.toLocaleString('ru-RU')}</strong>
            {' · '}Страница <strong>{pagination.page}</strong> из <strong>{pagination.totalPages}</strong>
            {' · '}По <strong>{DEFAULT_PAGE_LIMIT}</strong> на странице
          </Typography>
          <Pagination
            count={pagination.totalPages}
            page={page}
            onChange={(_, v) => onPageChange(v)}
            size="small"
            color="primary"
            showFirstButton
            showLastButton
          />
          <Typography variant="body2" color="text.secondary">
            Итого: <strong>{fmtSum(kpi?.totalSum ?? 0)}</strong>
            {' · '}
            <strong>
              {kpi?.totalQty.toLocaleString('ru-RU')} уп.
              {(kpi?.totalQtyPcs ?? 0) > 0 && ` / ${kpi?.totalQtyPcs.toLocaleString('ru-RU')} шт.`}
            </strong>
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default JournalTable;
